import React from "react";
import { useTranslation } from "react-i18next";
import { useNotification, usePoste } from "@hooks/index";
import moment from "moment-timezone";
import { useFormContext, Controller } from "react-hook-form";
import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import FormSimple from "@components/formGroup/formSimple";
import InputWithAddOptions from "@components/input/InputWithAddOptions";
import { CheckboxSimple, Input } from "@components/tailwindComponents";
import ListboxGroupe from "@components/tailwindComponents/ListboxGroupe/index";
import ListboxHierarchical from "@components/tailwindComponents/ListboxHierarchical";
import ListboxSimple from "@components/tailwindComponents/ListboxSimple";
import useCheckDuplicationUsers from "@pages/shared/useCheckDuplicationUsers";

const ACCESS_SETTINGS_WARNING_KEY = "employee_access_settings_readonly_warning";

function SectionJob(props) {
  const {
    numAlreadyExist
  } = useCheckDuplicationUsers({
    employeeId: props?.employeeId
  })
  const { t: mainTranslator } = useTranslation("pages.employés.emploi&Salary");
  const { showWarning } = useNotification();

  const { control, trigger, watch } = useFormContext();
  const { _insertPoste, _editPoste, _deletePoste } = usePoste();

  const showAccessSettingsWarning = () => {
    showWarning(ACCESS_SETTINGS_WARNING_KEY);
  };

  return (
    <div className="arh-flex arh-w-full arh-flex-wrap arh-items-start arh-pb-8 arh-pl-24 arh-pr-4">
      <div
        className={`arh-mr-[1.875rem] arh-w-[calc(33.33%-1.875rem)] arh-min-w-[11.5rem] ${props.readOnly && "arh-mb-[1.7rem]"}`}
      >
        <Controller
          control={control}
          name="poste"
          defaultValue=""
          rules={{ required: true }}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid }
          }) => (
            <>
              <label
                className={`arh-mb-1 arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${invalid ? "arh-text-invalidColorTextInput " : props.readOnly ? "arh-text-greyDisabledText " : "arh-text-darkColor "}`}
              >
                {mainTranslator("Poste*")}
              </label>
              {props.readOnly ? (
                <Input
                  name={name}
                  value={value?.title || ""}
                  placeholder={mainTranslator("Poste*")}
                  disabled
                  additionnalClas="arh-h-[44px]"
                />
              ) : (
                <InputWithAddOptions
                  defaultText={mainTranslator("Poste*")}
                  optionsList={props.postes}
                  secondDefaultText={mainTranslator("Nom poste")}
                  name={name}
                  defaultSelected={value?._id}
                  value={value?.title}
                  optionListLabel="title"
                  onChange={onChange}
                  addItem={_insertPoste}
                  editItem={_editPoste}
                  deleteItem={_deletePoste}
                  // showAction={true}
                  hideEdit={true}
                  classParentList="arh-mb-[1.75rem]"
                  validateDropdown={invalid}
                  disableInputChange={true}
                  withSearch
                />
              )}
            </>
          )}
        />
      </div>

      <div className="arh-mr-[1.875rem] arh-w-[calc(33.33%-1.875rem)] arh-min-w-[11.5rem]">
        <Controller
          control={control}
          name="job.hierarchical"
          defaultValue=""
          rules={{ required: true }}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <ListboxHierarchical
              placeholder={mainTranslator("Supérieur hiérarchique*")}
              name="supHierarchique"
              optionList={props.employeList || []}
              selectedValue={value}
              setSelectedValue={onChange}
              invalid={invalid}
              currentEmployeId={props.employeeId}
              classLabel="arh-flex"
              label={mainTranslator("Supérieur hiérarchique*")}
              classParentList="arh-mb-[1.75rem]"
              readOnly={props.readOnly || props.superiorReadonly}
            />
          )}
        />
      </div>
      <div className="arh-mr-[1.875rem] arh-w-[calc(33.33%-1.875rem)] arh-min-w-[11.5rem]">
        <Controller
          control={control}
          name="emplacement"
          defaultValue={[]}
          rules={{ validate: { required: (value) => !!value.length } }}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <ListboxSimple
              label={mainTranslator("Emplacement*")}
              name="emplacement"
              optionList={props.emplacementsList || []}
              selectedValue={value}
              setSelectedValue={onChange}
              multiple
              invalid={invalid}
              classLabel="arh-flex"
              placeholder={mainTranslator("Emplacement*")}
              classParentList="arh-mb-[1.75rem]"
              readOnly={props.readOnly}
            />
          )}
        />
      </div>
      {!props.employeeView && (
        <div
          className="arh-mr-[1.875rem] arh-w-[calc(33.33%-1.875rem)] arh-min-w-[11.5rem]"
          onClick={showAccessSettingsWarning}
        >
          <Controller
            control={control}
            name="permission"
            defaultValue=""
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { invalid } }) => (
              <ListboxSimple
                placeholder={mainTranslator("Permissions*")}
                name="permissions"
                optionList={props.permissionsList || []}
                optionTextAttribut="role"
                selectedValue={value}
                setSelectedValue={onChange}
                invalid={invalid}
                readOnly
                classLabel="arh-flex"
                label={mainTranslator("Permissions*")}
                classParentList="arh-mb-[1.75rem]"
              />
            )}
          />
        </div>
      )}
      {!props.employeeView && (
        <div
          className="arh-mr-[1.875rem] arh-w-[calc(33.33%-1.875rem)] arh-min-w-[11.5rem]"
          onClick={showAccessSettingsWarning}
        >
          <Controller
            control={control}
            name="accessProfile"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <ListboxSimple
                placeholder={mainTranslator("Profil d'acc\u00e8s")}
                name="accessProfile"
                optionList={props.accessProfilesList || []}
                optionTextAttribut="title"
                selectedValue={value}
                setSelectedValue={onChange}
                readOnly
                noRadio
                classLabel="arh-flex"
                label={mainTranslator("Profil d'acc\u00e8s")}
                classParentList="arh-mb-[1.75rem]"
              />
            )}
          />
        </div>
      )}
      <div className="arh-mr-[1.875rem] arh-w-[calc(33.33%-1.875rem)] arh-min-w-[11.5rem]">
        <Controller
          control={control}
          name="groupes"
          defaultValue={[]}
          render={({ field: { onChange, value } }) => (
            <ListboxGroupe
              placeholder={mainTranslator("Groupe")}
              name="Groupes"
              optionList={props.groupesList || []}
              selectedValue={value}
              setSelectedValue={onChange}
              classLabel="arh-flex"
              label={mainTranslator("Groupe")}
              classParentList="arh-mb-[1.75rem]"
              readOnly={props.readOnly}
            />
          )}
        />
      </div>
      <div className="arh-mr-[1.875rem] arh-w-[calc(33.33%-1.875rem)] arh-min-w-[11.5rem]">
        <Controller
          control={control}
          name="job.numEmployer"
          defaultValue=""
          rules={{ validate: { numAlreadyExist } }}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <FormSimple
              label={mainTranslator("Numéro de l'employé")}
              placeholder={mainTranslator("Numéro de l'employé")}
              ParentForm="arh-mb-[1.75rem]"
              classLabel="arh-flex"
              type="text"
              classInput="arh-h-[44px]"
              onChange={(e) => {
                onChange(e.target.value);
              }}
              value={value}
              invalid={invalid}
              readOnly={props.readOnly}
            />
          )}
        />
      </div>

      <div className="arh-mr-[1.875rem] arh-w-[calc(33.33%-1.875rem)] arh-min-w-[11.5rem]">
        <Controller
          control={control}
          name="job.hiringDate"
          defaultValue=""
          render={({
            field: { onChange, value, ref },
            fieldState: { invalid }
          }) => (
            <div
              className="flex-column calendar_with_btn employerThemeDatePicker wadeDatePicker arh-mb-[1.75rem] arh-flex arh-w-full"
              ref={ref}
            >
              <label
                className={`arh-mb-1 arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${invalid ? "arh-text-invalidColorTextInput " : props.readOnly ? "arh-text-greyDisabledText " : "arh-text-darkColor "}`}
              >
                {mainTranslator("Date d'embauche")}
              </label>
              {props.readOnly ? (
                <Input
                  name="selectedDate"
                  value={value ? moment(value).format("LL") : ""}
                  placeholder={mainTranslator("Date d'embauche")}
                  disabled
                  additionnalClas="arh-h-[44px]"
                />
              ) : (
                <DefaultDatePicker
                  className="focus_green dateOfBirth"
                  // name="selectedDate"
                  placeholder={mainTranslator("Employé depuis")}
                  dateValue={value}
                  setDateValue={onChange}
                  // classPosition="calendar_right"
                  invalid={invalid}
                  setSuperZIndex={props.setSuperZIndex}
                  isInForeground={true}
                  marginTop={0}
                  customClass="arh-bottom-12"
                />
              )}
            </div>
          )}
        />
      </div>

      <div className="arh-mr-[1.875rem] arh-w-[calc(33.33%-1.875rem)] arh-min-w-[11.5rem]">
        <Controller
          control={control}
          name="job.seniorityDate"
          defaultValue=""
          rules={{
            validate: {
              anciennete_required: (value) =>
                Boolean(!watch("job.defaultDateHiring") || value)
            }
          }}
          render={({
            field: { onChange, value, ref },
            fieldState: { invalid }
          }) => (
            <div
              className="calendar_with_btn employerThemeDatePicker wadeDatePicker arh-mb-1 arh-flex arh-w-full arh-flex-col"
              ref={ref}
            >
              <label
                className={`arh-mb-1 arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${invalid ? "arh-text-invalidColorTextInput " : props.readOnly ? "arh-text-greyDisabledText " : "arh-text-darkColor "}`}
              >
                {mainTranslator("Date d'ancienneté relative")}
              </label>
              {props.readOnly ? (
                <Input
                  name="selectedDate"
                  value={value ? moment(value).format("LL") : ""}
                  placeholder={mainTranslator("Date d'ancienneté")}
                  disabled
                  additionnalClas="arh-h-[44px]"
                />
              ) : (
                <DefaultDatePicker
                  className="focus_green dateOfBirth"
                  // name="selectedDateSeniority"
                  placeholder={mainTranslator("Date d'ancienneté")}
                  dateValue={value}
                  setDateValue={onChange}
                  // classPosition="calendar_right"
                  invalid={invalid}
                  setSuperZIndex={props.setSuperZIndex}
                  isInForeground={true}
                  marginTop={0}
                  customClass="arh-bottom-12"
                />
              )}
            </div>
          )}
        />
        <Controller
          control={control}
          name="job.defaultDateHiring"
          defaultValue={false}
          render={({ field: { onChange, value } }) => (
            <CheckboxSimple
              id="defaultDateHiring"
              label={mainTranslator(
                "Utiliser comme date d'embauche dans les statistiques et anniversaires"
              )}
              checked={value}
              noRadio={false}
              variant="green"
              recurrenceListBox={false}
              withTooltip={false}
              disabled={props.readOnly}
              className="arh-px-0 desktop-middle:arh-items-start"
              customClass="arh-ml-1.5"
              fontLabel="arh-text-fontBodyTable arh-font-normal arh-text-wrap"
              haveHover={false}
              onChange={(e) => {
                onChange(e.target.checked);
                if (!e.target.checked) trigger("job.seniorityDate");
              }}
            />
          )}
        />
      </div>
    </div>
  );
}

export default SectionJob;
